import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';

// Default values
const DEFAULT_AUTOMATED_PHRASES = [
  'Open me up to chat!',
  'Can I help you?',
  'Have questions?',
  'Want to know more?',
  'Need help?',
];

const BubbleCustomization = ({ customization, setCustomization, setHasUnsavedChanges, agentId }) => {
  // Closed Agent Text State - using showBubble variable
  const showBubble = customization.showBubble === true;

  // Show bubble in mobile state - default to false (unchecked)
  const showBubbleInMobile = customization.showBubbleInMobile === true;

  // Automated Phrases State - using radio button selection
  // Default to 'automated' (Predefined Phrases) if showBubble is enabled but no bubbleType is set
  const bubbleType = customization.bubbleType;
  const automatedClosedAgentEnabled = bubbleType === 'automated';
  const getInitialPhrases = () => {
    if (Array.isArray(customization.automatedClosedAgentPhrases)) {
      const phrases = customization.automatedClosedAgentPhrases.map(s => String(s || '').trim()).filter(Boolean);
      return phrases.length > 0 ? phrases : DEFAULT_AUTOMATED_PHRASES.slice();
    } else if (typeof customization.automatedClosedAgentPhrases === 'string') {
      const phrases = customization.automatedClosedAgentPhrases.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
      return phrases.length > 0 ? phrases : DEFAULT_AUTOMATED_PHRASES.slice();
    } else {
      return DEFAULT_AUTOMATED_PHRASES.slice();
    }
  };
  const initialAutomatedPhrases = getInitialPhrases();
  const [automatedPhrasesList, setAutomatedPhrasesList] = useState(initialAutomatedPhrases);
  const [automatedPhrasesError, setAutomatedPhrasesError] = useState('');


  // AI-based sitemap state
  const aiBasedEnabled = bubbleType === 'aiBased';
  const [domain, setDomain] = useState(customization.bubbleDomain || 'https://');
  const [loadingSitemap, setLoadingSitemap] = useState(false);
  const [matchedPages, setMatchedPages] = useState(customization.bubbleMatchedPages || []); // Array of {url, title, nudge, greeting}
  const [unmatchedUrls, setUnmatchedUrls] = useState(customization.bubbleUnmatchedUrls || []); // Array of URLs

  // Unmatched URL handling options
  const unmatchedUrlOption = customization.unmatchedUrlOption || 'hide';
  const [fallbackNudge, setFallbackNudge] = useState(
    customization.fallbackNudge || 'Click me if you have any question'
  );

  // Keep local list in sync if customization changes externally
  useEffect(() => {
    let arr;
    if (Array.isArray(customization.automatedClosedAgentPhrases)) {
      arr = customization.automatedClosedAgentPhrases.map(s => String(s || ''));
      // If all items are empty/whitespace and enabled, use defaults
      const nonEmptyItems = arr.filter(s => s.trim());
      if (nonEmptyItems.length === 0 && automatedClosedAgentEnabled) {
        arr = DEFAULT_AUTOMATED_PHRASES.slice();
      }
    } else if (typeof customization.automatedClosedAgentPhrases === 'string') {
      arr = customization.automatedClosedAgentPhrases.split(/\r?\n/).map(s => String(s || ''));
      // If all items are empty/whitespace and enabled, use defaults
      const nonEmptyItems = arr.filter(s => s.trim());
      if (nonEmptyItems.length === 0 && automatedClosedAgentEnabled) {
        arr = DEFAULT_AUTOMATED_PHRASES.slice();
      }
    } else {
      // If enabled but no phrases exist, use defaults
      arr = automatedClosedAgentEnabled ? DEFAULT_AUTOMATED_PHRASES.slice() : [];
    }
    setAutomatedPhrasesList(arr);
  }, [customization && JSON.stringify(customization.automatedClosedAgentPhrases), automatedClosedAgentEnabled]);

  // When the local list changes, write it back into customization (only if automated is selected and showBubble is enabled)
  useEffect(() => {
    if (automatedClosedAgentEnabled && showBubble) {
      setCustomization(prev => {
        const updated = { ...prev, automatedClosedAgentPhrases: automatedPhrasesList };
        // Ensure AI-based fields are cleared when saving predefined phrases
        delete updated.bubbleDomain;
        delete updated.unmatchedUrlOption;
        delete updated.fallbackNudge;
        delete updated.bubbleMatchedPages;
        delete updated.bubbleUnmatchedUrls;
        return updated;
      });
      setHasUnsavedChanges && setHasUnsavedChanges(true);
    }
  }, [automatedPhrasesList, automatedClosedAgentEnabled, showBubble]);

  // Keep matchedPages and unmatchedUrls in sync if customization changes externally
  useEffect(() => {
    if (aiBasedEnabled) {
      if (Array.isArray(customization.bubbleMatchedPages)) {
        setMatchedPages(customization.bubbleMatchedPages);
      }
      if (Array.isArray(customization.bubbleUnmatchedUrls)) {
        setUnmatchedUrls(customization.bubbleUnmatchedUrls);
      }
    }
  }, [customization && JSON.stringify(customization.bubbleMatchedPages), customization && JSON.stringify(customization.bubbleUnmatchedUrls), aiBasedEnabled]);

  // Initialize bubbleType to 'automated' (Predefined Phrases) if showBubble is enabled but no bubbleType exists
  useEffect(() => {
    if (showBubble && !customization.bubbleType) {
      setCustomization(prev => {
        const updated = {
          ...prev,
          bubbleType: 'automated'
        };
        // Initialize phrases if they don't exist
        if (!updated.automatedClosedAgentPhrases || (Array.isArray(updated.automatedClosedAgentPhrases) && updated.automatedClosedAgentPhrases.every(p => !String(p).trim()))) {
          updated.automatedClosedAgentPhrases = DEFAULT_AUTOMATED_PHRASES.slice();
          setAutomatedPhrasesList(DEFAULT_AUTOMATED_PHRASES.slice());
        }
        return updated;
      });
    }
  }, [showBubble, customization.bubbleType]);

  // Handler for manual switch (keep other options available)
  const handleClosedAgentTextSwitch = (e) => {
    const checked = e.target.checked;
    setCustomization(prev => {
      const updated = {
        ...prev,
        showBubble: checked
      };

      // If disabling, clear all bubble-related fields
      if (!checked) {
        delete updated.bubbleType;
        delete updated.automatedClosedAgentPhrases;
        delete updated.bubbleDomain;
        delete updated.unmatchedUrlOption;
        delete updated.fallbackNudge;
        delete updated.bubbleMatchedPages;
        delete updated.bubbleUnmatchedUrls;
      } else {
        // If enabling, set default to "Predefined Phrases" (automated) if no bubbleType exists
        if (!updated.bubbleType) {
          updated.bubbleType = 'automated';
          // Initialize phrases if they don't exist
          if (!updated.automatedClosedAgentPhrases || (Array.isArray(updated.automatedClosedAgentPhrases) && updated.automatedClosedAgentPhrases.every(p => !String(p).trim()))) {
            updated.automatedClosedAgentPhrases = DEFAULT_AUTOMATED_PHRASES.slice();
            // Update local state
            setAutomatedPhrasesList(DEFAULT_AUTOMATED_PHRASES.slice());
          }
        }
      }

      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return updated;
    });
  };

  // Handler for mobile bubble checkbox
  const handleShowBubbleInMobileChange = (e) => {
    const checked = e.target.checked;
    setCustomization(prev => {
      const updated = {
        ...prev,
        showBubbleInMobile: checked
      };
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return updated;
    });
  };

  // Handler for bubble type radio button selection
  const handleBubbleTypeChange = (event) => {
    const selectedType = event.target.value;
    setCustomization(prev => {
      // Only save bubble-related fields if showBubble is enabled
      if (!showBubble) {
        return prev;
      }

      let phrases = prev.automatedClosedAgentPhrases;
      let updatedCustomization = {
        ...prev,
        bubbleType: selectedType,
        showBubble: true,
      };

      if (selectedType === 'automated') {
        // Initialize phrases if needed
        if (!phrases || (Array.isArray(phrases) && phrases.every(p => !String(p).trim()))) {
          phrases = DEFAULT_AUTOMATED_PHRASES.slice();
        } else if (typeof phrases === 'string') {
          phrases = phrases.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
          if (phrases.length === 0) {
            phrases = DEFAULT_AUTOMATED_PHRASES.slice();
          }
        } else if (Array.isArray(phrases)) {
          phrases = phrases.map(s => String(s || '').trim()).filter(Boolean);
          if (phrases.length === 0) {
            phrases = DEFAULT_AUTOMATED_PHRASES.slice();
          }
        } else {
          phrases = DEFAULT_AUTOMATED_PHRASES.slice();
        }
        // Update local state
        setAutomatedPhrasesList(phrases);
        // Save predefined phrases and clear AI-based fields
        updatedCustomization.automatedClosedAgentPhrases = phrases;
        // Clear AI-based fields
        delete updatedCustomization.bubbleDomain;
        delete updatedCustomization.unmatchedUrlOption;
        delete updatedCustomization.fallbackNudge;
        delete updatedCustomization.bubbleMatchedPages;
        delete updatedCustomization.bubbleUnmatchedUrls;
      } else if (selectedType === 'aiBased') {
        // Clear predefined phrases and keep AI-based fields
        delete updatedCustomization.automatedClosedAgentPhrases;
        // Initialize AI-based fields if needed
        if (!updatedCustomization.bubbleDomain) {
          updatedCustomization.bubbleDomain = '';
        }
        if (!updatedCustomization.unmatchedUrlOption) {
          updatedCustomization.unmatchedUrlOption = 'hide';
        }
        if (!updatedCustomization.fallbackNudge) {
          updatedCustomization.fallbackNudge = 'Click me if you have any question';
        }
      } else {
        // 'none' - clear both
        delete updatedCustomization.automatedClosedAgentPhrases;
        delete updatedCustomization.bubbleDomain;
        delete updatedCustomization.unmatchedUrlOption;
        delete updatedCustomization.fallbackNudge;
        delete updatedCustomization.bubbleMatchedPages;
        delete updatedCustomization.bubbleUnmatchedUrls;
      }

      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return updatedCustomization;
    });
  };

  // Handle Use Sitemap button click
  const handleUseSitemap = async () => {
    if (!domain || !domain.trim()) {
      alert('Please enter a domain');
      return;
    }
    if (!agentId) {
      alert('Agent ID is required');
      return;
    }

    setLoadingSitemap(true);
    try {
      const response = await axios.post('/kbs/generateBubbleNudges', {
        domain: domain.trim(),
        agentId: agentId
      });

      if (response.data.success) {
        const pages = response.data.matchedPages || [];
        const urls = response.data.unmatchedUrls || [];
        setMatchedPages(pages);
        setUnmatchedUrls(urls);
        // Update customization - save matched pages and clear predefined phrases when using AI-based (only if enabled)
        if (showBubble) {
          setCustomization(prev => {
            const updated = {
              ...prev,
              bubbleMatchedPages: pages,
              bubbleUnmatchedUrls: urls
            };
            // Clear predefined phrases when using AI-based option
            delete updated.automatedClosedAgentPhrases;
            return updated;
          });
          setHasUnsavedChanges && setHasUnsavedChanges(true);
        }
      } else {
        alert(response.data.message || 'Failed to generate nudges');
      }
    } catch (error) {
      console.error('Error generating nudges:', error);
      alert('Failed to generate nudges. Please try again.');
    } finally {
      setLoadingSitemap(false);
    }
  };


  useEffect(() => {
    if (automatedClosedAgentEnabled) {
      const arr = (automatedPhrasesList || []).map(s => String(s || '').trim()).filter(Boolean);
      if (!arr.length) {
        setAutomatedPhrasesError('At least one phrase is required.');
      } else {
        setAutomatedPhrasesError('');
      }
    } else {
      setAutomatedPhrasesError('');
    }
  }, [automatedClosedAgentEnabled, automatedPhrasesList]);

  const handlePhraseChange = (idx, value) => {
    const next = [...(automatedPhrasesList || [])];
    next[idx] = value;
    setAutomatedPhrasesList(next);
    setHasUnsavedChanges && setHasUnsavedChanges(true);
  };

  const handleAddAutomated = () => {
    const next = [...(automatedPhrasesList || []), ''];
    setAutomatedPhrasesList(next);
    setHasUnsavedChanges && setHasUnsavedChanges(true);
  };

  const handleDeleteAutomated = (idx) => {
    const next = (automatedPhrasesList || []).filter((_, i) => i !== idx);
    setAutomatedPhrasesList(next);
    setHasUnsavedChanges && setHasUnsavedChanges(true);
  };

  // Handle nudge change in matched pages table
  const handleNudgeChange = (idx, newNudge) => {
    const updatedPages = [...matchedPages];
    updatedPages[idx] = { ...updatedPages[idx], nudge: newNudge };
    setMatchedPages(updatedPages);
    // Save updated matched pages to customization (only if enabled)
    if (showBubble) {
      setCustomization(prev => {
        const updated = { ...prev, bubbleMatchedPages: updatedPages };
        // Clear predefined phrases when updating AI-based fields
        delete updated.automatedClosedAgentPhrases;
        return updated;
      });
      setHasUnsavedChanges && setHasUnsavedChanges(true);
    }
  };

  // Handle greeting change in matched pages table
  const handleGreetingChange = (idx, newGreeting) => {
    const updatedPages = [...matchedPages];
    updatedPages[idx] = { ...updatedPages[idx], greeting: newGreeting };
    setMatchedPages(updatedPages);
    // Save updated matched pages to customization (only if enabled)
    if (showBubble) {
      setCustomization(prev => {
        const updated = { ...prev, bubbleMatchedPages: updatedPages };
        // Clear predefined phrases when updating AI-based fields
        delete updated.automatedClosedAgentPhrases;
        return updated;
      });
      setHasUnsavedChanges && setHasUnsavedChanges(true);
    }
  };

  // Handle delete matched page
  const handleDeleteMatchedPage = (idx) => {
    const updatedPages = matchedPages.filter((_, i) => i !== idx);
    setMatchedPages(updatedPages);
    // Save updated matched pages to customization (only if enabled)
    if (showBubble) {
      setCustomization(prev => {
        const updated = { ...prev, bubbleMatchedPages: updatedPages };
        // Clear predefined phrases when updating AI-based fields
        delete updated.automatedClosedAgentPhrases;
        return updated;
      });
      setHasUnsavedChanges && setHasUnsavedChanges(true);
    }
  };

  return (
    <>
      <Box sx={{ p: 1 }}>
        {/* Closed Agent Text */}
        <FormControl fullWidth sx={{ mb: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
            <Typography>Enable Closed Agent Text Bubble</Typography>
            <Switch
              checked={showBubble}
              onChange={handleClosedAgentTextSwitch}
              color="primary"
            />
          </Box>
        </FormControl>

        {/* Show these options only when Closed Agent Text Bubble is enabled */}
        {showBubble && (
          <>
            {/* Show bubble in mobile checkbox */}
            <FormControl fullWidth sx={{ mb: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showBubbleInMobile}
                    onChange={handleShowBubbleInMobileChange}
                    color="primary"
                  />
                }
                label="Display text bubbles on mobile"
              />
              <Typography variant="caption" color="text.secondary">
                Note: The bubble occupies significant screen space on mobile devices. Consider disabling this option to maximize available screen area.
              </Typography>
            </FormControl>

            {/* Automated closed-agent phrases and AI-based Text Bubble - radio buttons */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 0.5 }}>
              <RadioGroup
                value={bubbleType}
                onChange={handleBubbleTypeChange}
                row
                sx={{ justifyContent: 'flex-start', gap: 2 }}
              >
                <FormControlLabel
                  value="automated"
                  control={<Radio size="small" />}
                  label="Predefined Phrases"
                />
                <FormControlLabel
                  value="aiBased"
                  control={<Radio size="small" />}
                  label="Use AI to create text based on Page title"
                />
              </RadioGroup>
            </FormControl>
            {automatedClosedAgentEnabled && (
              <Box>
                <Typography variant="body2" align='left' sx={{ mb: 0.5 }}>
                  Add up to 5 automated phrases. One phrase is picked at random when rendering.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.25, width: '100%' }}>
                  {(automatedPhrasesList || []).map((phrase, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 0.25,
                        width: '100%',
                        minWidth: 0,
                        gap: 1
                      }}
                    >
                      <TextField
                        fullWidth
                        value={phrase || ''}
                        onChange={(e) => handlePhraseChange(idx, e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 600 }}
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAutomated(idx)}
                        sx={{ color: 'error.main', flexShrink: 0 }}
                        aria-label="Delete phrase"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  {automatedPhrasesList.length < 5 && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddAutomated}
                      fullWidth
                      sx={{ mt: 0.25 }}
                    >
                      Add Phrase
                    </Button>
                  )}
                </Box>

                {automatedPhrasesError && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.25 }}>{automatedPhrasesError}</Typography>
                )}
              </Box>
            )}
            {aiBasedEnabled && (
              <Box>
                <Typography variant="body2" align='left' sx={{ mb: 0.5 }}>
                  Enter your domain and click "Use Sitemap" to automatically generate nudges based on page titles from your knowledge base.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
                  <TextField
                    label="Domain"
                    value={domain}
                    onChange={(e) => {
                      setDomain(e.target.value);
                      if (showBubble) {
                        setCustomization(prev => {
                          const updated = { ...prev, bubbleDomain: e.target.value };
                          // Clear predefined phrases when updating AI-based fields
                          delete updated.automatedClosedAgentPhrases;
                          return updated;
                        });
                        setHasUnsavedChanges && setHasUnsavedChanges(true);
                      }
                    }}
                    placeholder="https://example.com"
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleUseSitemap}
                    disabled={loadingSitemap || !domain || !domain.trim()}
                    sx={{ mt: 0.5 }}
                  >
                    {loadingSitemap ? <CircularProgress size={24} /> : 'Use Sitemap'}
                  </Button>
                </Box>

                {matchedPages.length > 0 && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Pages found in Agent Knowledgebase(s)</Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 1, mb: 1 }}>
                      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '11%', minWidth: 110 }}>URL</TableCell>
                            <TableCell sx={{ width: '14%', minWidth: 110 }}>Title</TableCell>
                            <TableCell sx={{ width: '32%', minWidth: 230 }}>Nudge</TableCell>
                            <TableCell sx={{ width: '35%', minWidth: 230 }}>AI Agent Greeting</TableCell>
                            <TableCell sx={{ width: '8%', minWidth: 60 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {matchedPages.map((page, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Tooltip title={page.url || ''} arrow placement="top">
                                  <span>{page.url}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Tooltip title={page.title || ''} arrow placement="top">
                                  <span>{page.title}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  value={page.nudge || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 60) {
                                      handleNudgeChange(idx, value);
                                    }
                                  }}
                                  variant="outlined"
                                  size="small"
                                  inputProps={{ maxLength: 60 }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.875rem'
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  value={page.greeting || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 600) {
                                      handleGreetingChange(idx, value);
                                    }
                                  }}
                                  variant="outlined"
                                  size="small"
                                  inputProps={{ maxLength: 600 }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.875rem'
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteMatchedPage(idx)}
                                  sx={{ color: 'error.main' }}
                                  aria-label="Delete nudge"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Unmatched URL handling options */}
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Unmatched URL Handling:
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={unmatchedUrlOption}
                      onChange={(e) => {
                        if (showBubble) {
                          setCustomization(prev => {
                            const updated = { ...prev, unmatchedUrlOption: e.target.value };
                            // Clear predefined phrases when updating AI-based fields
                            delete updated.automatedClosedAgentPhrases;
                            return updated;
                          });
                          setHasUnsavedChanges && setHasUnsavedChanges(true);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="hide"
                        control={<Radio size="small" />}
                        label="Do not show bubble if the URL doesn't match"
                      />
                      <FormControlLabel
                        value="fallback"
                        control={<Radio size="small" />}
                        label="Fallback if URL doesn't have Nudge"
                      />
                    </RadioGroup>
                  </FormControl>
                  {unmatchedUrlOption === 'fallback' && (
                    <TextField
                      fullWidth
                      label="Fallback Nudge"
                      value={fallbackNudge}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFallbackNudge(value);
                        if (showBubble) {
                          setCustomization(prev => {
                            const updated = { ...prev, fallbackNudge: value };
                            // Clear predefined phrases when updating AI-based fields
                            delete updated.automatedClosedAgentPhrases;
                            return updated;
                          });
                          setHasUnsavedChanges && setHasUnsavedChanges(true);
                        }
                      }}
                      placeholder="Click me if you have any question"
                      sx={{ mt: 1 }}
                      inputProps={{ maxLength: 600 }}
                    />
                  )}
                </Box>

                {unmatchedUrls.length > 0 && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      The URLs below are not found in your Knowledge Base. To generate AI nudges for these pages, add them to your knowledge base and return to this screen.
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300, mt: 1, mb: 1 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>URL</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {unmatchedUrls.map((url, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ wordBreak: 'break-word' }}>{url}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default BubbleCustomization;

